import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Custom React hook for scroll-snap carousel state management.
 * Manages refs, active slide tracking via IntersectionObserver, and optional autoplay.
 *
 * @param {Object} options - Carousel configuration options
 * @param {number} options.itemsLength - Number of slides/items in the carousel
 * @param {boolean} [options.autoPlay=false] - Whether to auto-advance slides
 * @param {number} [options.interval=4000] - Autoplay interval in milliseconds
 * @param {boolean} [options.loop=false] - Whether to wrap from last to first slide (and vice versa)
 * @param {number} [options.threshold=0.5] - IntersectionObserver threshold (0-1) for determining active slide
 * @returns {Object} Carousel state and controls
 * @returns {React.RefObject} return.containerRef - Ref to attach to the scroll container
 * @returns {React.RefObject} return.slideRefs - Ref to array of slide elements (use ref callback: (el) => slideRefs.current[i] = el)
 * @returns {number} return.activeIndex - Index of the currently active/visible slide
 * @returns {Function} return.scrollToSlide - (index: number) => void
 * @returns {Function} return.scrollToNext - () => void
 * @returns {Function} return.scrollToPrev - () => void
 * @returns {Function} return.setIsPaused - (paused: boolean) => void - Pause/resume autoplay
 * @returns {boolean} return.isPrevDisabled - True when prev button should be disabled
 * @returns {boolean} return.isNextDisabled - True when next button should be disabled
 */
export function useCarousel({
  itemsLength,
  autoPlay = false,
  interval = 4000,
  loop = false,
  threshold = 0.5,
}) {
  const containerRef = useRef(null)
  const slideRefs = useRef([])
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  // Tracks the canonical "intended" active index. Used by nav callbacks so they
  // are not affected by transient IntersectionObserver updates that fire during
  // a smooth scroll (which would otherwise make the first click after a
  // direction change appear to do nothing).
  const activeIndexRef = useRef(0)
  // When true, IntersectionObserver updates are ignored because we are in the
  // middle of a programmatic scroll. Cleared on `scrollend` (or via timeout).
  const isProgrammaticScrollRef = useRef(false)
  const programmaticScrollTimerRef = useRef(null)
  const releaseListenerRef = useRef(null)

  const updateActiveIndex = useCallback((index) => {
    activeIndexRef.current = index
    setActiveIndex(index)
  }, [])

  // Reset slideRefs when items length decreases to avoid stale refs
  useEffect(() => {
    slideRefs.current = slideRefs.current.slice(0, itemsLength)
  }, [itemsLength])

  // Track active slide using IntersectionObserver
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScrollRef.current) return
        const intersecting = entries.filter((e) => e.isIntersecting)
        if (intersecting.length === 0) return

        const byRatio = [...intersecting].sort(
          (a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0)
        )
        const best = byRatio[0]
        const index = Number(best.target.getAttribute('data-index'))
        if (!isNaN(index)) updateActiveIndex(index)
      },
      { root: container, threshold }
    )

    const currentSlideRefs = slideRefs.current
    currentSlideRefs.forEach((slide) => {
      if (slide) observer.observe(slide)
    })

    return () => {
      currentSlideRefs.forEach((slide) => {
        if (slide) observer.unobserve(slide)
      })
    }
  }, [threshold, itemsLength, updateActiveIndex])

  const scrollToSlide = useCallback(
    (index) => {
      if (!containerRef.current || !slideRefs.current[index]) return
      const container = containerRef.current
      const slide = slideRefs.current[index]
      // Center the slide within the container so it matches `snap-center` snap points.
      // Without centering, the browser will snap to the nearest snap-point, which can
      // cause prev/next to appear to move in the wrong direction.
      const slideCenter = slide.offsetLeft - container.offsetLeft + slide.clientWidth / 2
      const targetScrollLeft = Math.max(0, slideCenter - container.clientWidth / 2)

      // Always set the canonical index first so chevron disabled state and the
      // next scrollToNext/Prev call are correct, even if the browser ignores
      // the scroll (e.g., already at target) and `scrollend` never fires.
      updateActiveIndex(index)

      // Clean up any pending release from a previous in-flight programmatic
      // scroll so listeners don't accumulate and prematurely clear the flag.
      if (releaseListenerRef.current) {
        container.removeEventListener('scrollend', releaseListenerRef.current)
        releaseListenerRef.current = null
      }
      if (programmaticScrollTimerRef.current) {
        clearTimeout(programmaticScrollTimerRef.current)
        programmaticScrollTimerRef.current = null
      }

      // If we are already at the target position, skip the suppression dance
      // entirely — no scroll will happen and `scrollend` will not fire.
      if (Math.abs(container.scrollLeft - targetScrollLeft) < 1) {
        isProgrammaticScrollRef.current = false
        return
      }

      isProgrammaticScrollRef.current = true
      container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' })

      const release = () => {
        isProgrammaticScrollRef.current = false
        if (releaseListenerRef.current) {
          container.removeEventListener('scrollend', releaseListenerRef.current)
          releaseListenerRef.current = null
        }
        if (programmaticScrollTimerRef.current) {
          clearTimeout(programmaticScrollTimerRef.current)
          programmaticScrollTimerRef.current = null
        }
      }
      releaseListenerRef.current = release
      container.addEventListener('scrollend', release, { once: true })
      programmaticScrollTimerRef.current = setTimeout(release, 800)
    },
    [updateActiveIndex]
  )

  // Compute the index whose snap-center is currently closest to the container's
  // visual center. This is more reliable than `activeIndexRef` for nav actions
  // because the container's scroll position can drift from our state (touch
  // gestures, wheel scrolling, or consumers that imperatively set
  // `container.scrollLeft` on mount before the IntersectionObserver has a
  // chance to update us).
  const getCurrentIndexFromScroll = useCallback(() => {
    const container = containerRef.current
    if (!container) return activeIndexRef.current
    const viewportCenter = container.scrollLeft + container.clientWidth / 2
    let bestIndex = activeIndexRef.current
    let bestDistance = Infinity
    for (let i = 0; i < slideRefs.current.length; i++) {
      const slide = slideRefs.current[i]
      if (!slide) continue
      const slideCenter = slide.offsetLeft - container.offsetLeft + slide.clientWidth / 2
      const distance = Math.abs(slideCenter - viewportCenter)
      if (distance < bestDistance) {
        bestDistance = distance
        bestIndex = i
      }
    }
    return bestIndex
  }, [])

  const scrollToNext = useCallback(() => {
    const current = getCurrentIndexFromScroll()
    if (current < itemsLength - 1) {
      scrollToSlide(current + 1)
    } else if (loop) {
      scrollToSlide(0)
    }
  }, [itemsLength, loop, scrollToSlide, getCurrentIndexFromScroll])

  const scrollToPrev = useCallback(() => {
    const current = getCurrentIndexFromScroll()
    if (current > 0) {
      scrollToSlide(current - 1)
    } else if (loop) {
      scrollToSlide(itemsLength - 1)
    }
  }, [loop, itemsLength, scrollToSlide, getCurrentIndexFromScroll])

  // Autoplay functionality
  useEffect(() => {
    if (!autoPlay || isPaused) return
    const timer = setInterval(() => {
      scrollToNext()
    }, interval)
    return () => clearInterval(timer)
  }, [autoPlay, isPaused, scrollToNext, interval])

  return {
    containerRef,
    slideRefs,
    activeIndex,
    scrollToSlide,
    scrollToNext,
    scrollToPrev,
    setIsPaused,
    isPrevDisabled: !loop && activeIndex === 0,
    isNextDisabled: !loop && activeIndex === itemsLength - 1,
  }
}

export default useCarousel
